/**
 * Gesturely — Paddle Frontend Configuration
 *
 * This module centralizes ALL Paddle initialization and checkout logic.
 * Switching from Sandbox to Live requires only changing environment variables:
 *   VITE_PADDLE_ENVIRONMENT=production
 *   VITE_PADDLE_CLIENT_TOKEN=live_...
 *   VITE_PADDLE_PRICE_ID=pri_live_...
 *
 * SECURITY NOTES:
 *   - Only Paddle client-side tokens belong here.
 *   - Never import or use: API keys, webhook secrets, Supabase keys,
 *     Resend keys, license signing keys, or any other server-side secret.
 *   - License fulfillment is handled EXCLUSIVELY by the server-side
 *     Paddle webhook — this module only handles the checkout UI.
 */

import { initializePaddle, type Paddle, type CheckoutEventNames } from '@paddle/paddle-js'

// ── Environment config ──────────────────────────────────────────

type PaddleEnvironment = 'sandbox' | 'production'

interface PaddleConfig {
  environment: PaddleEnvironment
  clientToken: string
  priceId: string
}

export interface GesturelyPricePreview {
  formattedTotal: string
  currencyCode: string
  countryCode: string | null
}

function getPaddleConfig(): PaddleConfig {
  const rawEnvironment = import.meta.env.VITE_PADDLE_ENVIRONMENT ?? 'sandbox'
  const environment: PaddleEnvironment = rawEnvironment === 'production' ? 'production' : 'sandbox'
  const clientToken = import.meta.env.VITE_PADDLE_CLIENT_TOKEN ?? ''
  const priceId = import.meta.env.VITE_PADDLE_PRICE_ID ?? ''

  if (rawEnvironment !== 'sandbox' && rawEnvironment !== 'production') {
    console.error(`[Gesturely] Unsupported Paddle environment: ${rawEnvironment}`)
  }
  if (environment === 'production' && clientToken.startsWith('test_')) {
    console.error('[Gesturely] Production is configured with a Sandbox Paddle client token.')
  }
  if (environment === 'sandbox' && clientToken.startsWith('live_')) {
    console.error('[Gesturely] Sandbox is configured with a Live Paddle client token.')
  }

  if (!clientToken) {
    console.warn('[Gesturely] VITE_PADDLE_CLIENT_TOKEN is not set. Checkout will not work.')
  }
  if (!priceId) {
    console.warn('[Gesturely] VITE_PADDLE_PRICE_ID is not set. Checkout will not work.')
  }

  return { environment, clientToken, priceId }
}

export const paddleConfig = getPaddleConfig()

export function isPaddleConfigurationValid(): boolean {
  const { environment, clientToken, priceId } = paddleConfig
  if (!clientToken || !priceId) return false
  if (environment === 'production' && clientToken.startsWith('test_')) return false
  if (environment === 'sandbox' && clientToken.startsWith('live_')) return false
  return true
}

// ── Singleton Paddle instance ───────────────────────────────────

let paddleInstance: Paddle | null = null
let initPromise: Promise<Paddle | null> | null = null

/**
 * Module-level callback slot for the active checkout's completion handler.
 *
 * This is the bridge between the centralized eventCallback (which Paddle calls)
 * and the UI component that needs to react to checkout.completed.
 *
 * Only one checkout can be open at a time, so a single slot is sufficient.
 * The UI sets this before opening the overlay and clears it after firing.
 */
let onCheckoutCompleteCallback: (() => void) | null = null
let onCheckoutCloseCallback: (() => void) | null = null

/**
 * Register a one-shot callback to be called when Paddle fires checkout.completed.
 * Called by openGesturelyCheckout before opening the overlay.
 */
function registerCheckoutCompleteCallback(cb: () => void): void {
  onCheckoutCompleteCallback = cb
}

function registerCheckoutCloseCallback(cb: () => void): void {
  onCheckoutCloseCallback = cb
}

/**
 * Initialize Paddle lazily (called once, returns cached instance thereafter).
 * Safe to call multiple times — subsequent calls return the same promise/instance.
 *
 * Strict Mode safe: the initPromise guard prevents double-initialization even
 * when React mounts effects twice in development.
 */
export async function initPaddle(): Promise<Paddle | null> {
  if (paddleInstance) return paddleInstance

  // Guard against parallel double-initialization (React Strict Mode, etc.)
  if (initPromise) return initPromise

  if (!paddleConfig.clientToken) {
    console.error('[Gesturely] Cannot initialize Paddle: client token is missing.')
    return null
  }

  initPromise = initializePaddle({
    environment: paddleConfig.environment,
    token: paddleConfig.clientToken,

    /**
     * Official Paddle event callback — the ONLY place we receive Paddle events.
     * No window.message / postMessage listeners are used anywhere in this codebase.
     */
    eventCallback(event) {
      if (import.meta.env.DEV) {
        console.log('[Paddle event]', event.name, event.data)
      }

      // checkout.completed — UI feedback only.
      // License fulfillment is handled exclusively by the server-side Paddle webhook.
      if ((event.name as CheckoutEventNames) === 'checkout.completed') {
        if (onCheckoutCompleteCallback) {
          onCheckoutCompleteCallback()
        }
        onCheckoutCompleteCallback = null
        onCheckoutCloseCallback = null
      }

      // A buyer may close the overlay without purchasing. Restore the CTA
      // immediately instead of leaving the page stuck on "Opening checkout…".
      if ((event.name as CheckoutEventNames) === 'checkout.closed') {
        onCheckoutCloseCallback?.()
        onCheckoutCompleteCallback = null
        onCheckoutCloseCallback = null
      }
    },
  }).then((paddle) => {
    if (paddle) {
      paddleInstance = paddle
    } else {
      console.error('[Gesturely] Paddle initialization returned null.')
    }
    return paddle ?? null
  }).catch((err: unknown) => {
    console.error('[Gesturely] Paddle initialization failed:', err)
    initPromise = null  // Allow retry on next call
    return null
  })

  return initPromise
}

/**
 * Ask Paddle for the buyer-localized total shown for the configured price.
 * Paddle remains the source of truth for currency conversion and checkout
 * availability; the website falls back to the canonical USD label if preview
 * lookup is unavailable.
 */
export async function getGesturelyPricePreview(): Promise<GesturelyPricePreview | null> {
  const paddle = await initPaddle()
  if (!paddle || !paddleConfig.priceId) return null

  try {
    const preview = await paddle.PricePreview({
      items: [{ priceId: paddleConfig.priceId, quantity: 1 }],
    })
    const lineItem = preview.data.details.lineItems[0]
    if (!lineItem) return null
    return {
      formattedTotal: lineItem.formattedTotals.total,
      currencyCode: preview.data.currencyCode,
      countryCode: preview.data.address?.countryCode ?? null,
    }
  } catch (err: unknown) {
    if (import.meta.env.DEV) {
      console.warn('[Gesturely] Paddle price preview failed; using the USD fallback.', err)
    }
    return null
  }
}

/**
 * Open Paddle overlay checkout for Gesturely.
 * Always uses quantity 1.
 *
 * Completion is detected via Paddle's official eventCallback registered at
 * initializePaddle() time. No window.message / postMessage listeners are used.
 *
 * @param onComplete - Called when Paddle fires checkout.completed (UI only).
 *                     License fulfillment happens server-side via webhook.
 * @param onError    - Called if checkout cannot be opened.
 */
export async function openGesturelyCheckout(
  onComplete?: () => void,
  onError?: (err: Error) => void,
  onClose?: () => void
): Promise<void> {
  const { priceId } = paddleConfig

  if (!priceId) {
    onError?.(new Error('Price ID is not configured.'))
    return
  }

  const paddle = await initPaddle()

  if (!paddle) {
    onError?.(new Error('Could not initialize the payment system. Please try again.'))
    return
  }

  // Register the UI completion callback BEFORE opening the overlay,
  // so eventCallback can fire it as soon as checkout.completed arrives.
  if (onComplete) {
    registerCheckoutCompleteCallback(onComplete)
  }
  if (onClose) {
    registerCheckoutCloseCallback(onClose)
  }

  try {
    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      settings: {
        displayMode: 'overlay',
      },
      customData: {
        product: 'gesturely',
        source: 'website_buy_page',
      },
    })
  } catch (err) {
    // Opening failed — clear the pending callback so it doesn't fire spuriously
    onCheckoutCompleteCallback = null
    onCheckoutCloseCallback = null
    const error = err instanceof Error ? err : new Error('Checkout could not be opened.')
    onError?.(error)
  }
}
